import { organizationApi } from '@/api/account-management/organizationApi';
import { HttpError } from '@/api/common';
import { createOrganization, type Organization } from '@/models/account-management/Organization';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { DataGrid, type GridColDef, type GridRowParams, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { fillHeight } from "../../theme";
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Dialog from '../common/dialog/Dialog';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InviteUserDialog from './InviteUserDialog';
import FormTextField from '../common/FormTextField';

export interface OrganizationListProps {
    onSelect: (id: string | undefined) => void;
    selectedId?: string;
}

const OrganizationList = ({ onSelect, selectedId }: OrganizationListProps) => {
    // Controlled selection so the row stays highlighted (e.g. when returning from BrandDetails)
    const rowSelectionModel: GridRowSelectionModel = useMemo(
        () => ({ type: 'include', ids: new Set(selectedId ? [selectedId] : []) }),
        [selectedId]
    );
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(undefined); // Currently selected organization id
    const [dialogOpen, setDialogOpen] = useState(false); // Dialog open state
    const [dialogTitleName, setDialogTitleName] = useState<string | undefined>(); // Item name to display in dialog title (Edit <Name>) or undefined if add new item
    const isEditing = useMemo(() => dialogTitleName !== undefined, [dialogTitleName]); // Nice way to determine if we are editing (based on if we have an org name)
    const [userDialogOpen, setUserDialogOpen] = useState(false); // Invite user dialog open state
    const { reset, register, handleSubmit: onFormSubmit, formState } = useForm<Organization>(); // react-hook-form objects    
    const qc = useQueryClient();
    const { data: organizationList, isPending: isGridLoading } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: organizationApi.list, staleTime: Infinity }); // react-query to fetch the list of organizations
    const { mutateAsync: saveOrganization, isPending: isSaving } = useMutation({ mutationFn: organizationApi.save, onSuccess: () => handleSaveSuccess(), onError: () => handleSaveError() }); //react-query to save the organization (update or create)
    const { mutateAsync: deleteOrganization } = useMutation({ mutationFn: organizationApi.delete, onSuccess: () => handleDeleteSuccess(), onError: (error) => handleDeleteError(error) }); //react-query to delete the organization
    const toast = useToast();
    const strings = useLocalizedStrings();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.adminConfiguration);
    const canUpdate = perms.canUpdate(Perm.adminConfiguration);
    const canDelete = perms.canDelete(Perm.adminConfiguration);
    // Inviting someone is user management, not org configuration, so it answers to the permission that
    // governs creating users everywhere else rather than to this screen's.
    const canInvite = perms.canCreate(Perm.users);
    // Creating and editing are separate grants: gating both on canUpdate would hide Save from someone who
    // may create but not amend. Same rule as UserList.
    const dialogReadOnly = isEditing ? !canUpdate : !canCreate;

    // Grid columnn definitions
    const columns: GridColDef<Organization>[] = [        
        {
            field: 'name',
            headerName: strings.name,
            flex: 1,
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [                
                <DataGridEditButton id={params.id.toString()} onClick={handleEditClick} aria-label="edit organization" readOnly={!canUpdate} />,
                ...(canInvite ? [
                <Tooltip title={strings.inviteUser}>
                    <IconButton onClick={(_) => handleAddUserClick(params.id.toString())} aria-label="organization add user">
                        <PersonAddIcon color="secondary" />
                    </IconButton>
                </Tooltip>] : []),
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={getDeleteWarningMessage} onClick={handleDeleteClick} aria-label="delete organization" hidden={!canDelete} />
            ],
        },
    ];
    
    const handleRowSelectionModelChange = ({ ids }: GridRowSelectionModel) => {
        const id = ids.values().next().value as string;        
        if (!id) return;
        onSelect(id);
    };

    // Reset dialog
    useEffect(() => {
        if (!dialogOpen) {
            setDialogTitleName(undefined);
            reset(undefined);
        }
    }, [dialogOpen, reset]);     

    // Add button handler
    const handleAddClick = () => {
        reset(createOrganization()); // reset() is how you set the instance of the form object (in this case a new empty Organization)
        setDialogOpen(true);
    };

    // Edit (row) button handler
    const handleEditClick = (id: string) => {
        const org = organizationList?.find(o => o.id === id);
        if (!org) return;
        reset(org); // Set the instance of the form object to the selected organization        
        setDialogTitleName(org.name);
        setDialogOpen(true);
    };

    const handleAddUserClick = (id: string) => {
        const org = organizationList?.find(o => o.id === id);
        if (!org) return;
        setSelectedOrganizationId(org.id);
        setUserDialogOpen(true);
    };

    const handleUserClose = () => {
        setSelectedOrganizationId(undefined);
        setUserDialogOpen(false);
    };

    // Get the warning message to display when user tries to delete a row
    const getDeleteWarningMessage = (id: string) => {
        const org = organizationList?.find(o => o.id === id);
        return strings.organizationConfirmDeleteMessgage.replace("{{name}}", org?.name ?? "");
    }   

    // Delete (row) button handler
    const handleDeleteClick = async (id: string) => {
        // mutateAsync rejects as well as calling onError, and a refused delete is now an ordinary outcome
        // rather than a rarity — without this every refusal also raised an unhandled rejection. The toast in
        // handleDeleteError is the report; there is nothing further to do here.
        try {
            await deleteOrganization(id); // Run the delete mutation for the selecetd row
        } catch {
            // reported by onError
        }
    };
    
    const handleDeleteSuccess = async () => {
		toast({ severity: "success", message: strings.organizationDeleteSuccess });		
        await qc.invalidateQueries({ queryKey: ['organizations'] }); // Invalidate the organizations query to refresh the list
        onSelect(undefined); // Clear the selected organization
		setDialogOpen(false);
	}

	// Deleting an organization is refused while it still has brands or users, and the API says exactly what is
	// in the way ("still has 2 brand(s) and 3 user(s)"). Showing a fixed string instead threw that away and
	// left the admin with "delete failed" and nothing to act on. Falls back to the localized wording when the
	// server sent no detail — an undefined detail means it did not explain, not that anything succeeded.
	const handleDeleteError = (error: unknown) => {
		const detail = error instanceof HttpError ? error.detail : undefined;
		toast({ severity: "error", message: detail ?? strings.organizationDeleteError });
	}

    // Dialog form save/submit button handler (data has the current values of the form fields)
    const handleSubmit: SubmitHandler<Organization> = async (data) => {
        console.log(data);                
        await saveOrganization(data); // Run the save mutation with the current form data
    }

    const handleSaveSuccess = async () => {
		toast({ severity: "success", message: strings.organizationSaveSuccess });		
        await qc.invalidateQueries({ queryKey: ['organizations'] }); // Invalidate the organizations query to refresh the list
        onSelect(undefined);
		setDialogOpen(false);
	}

	const handleSaveError = () => {
		toast({ severity: "error", message: strings.organizationSaveError });
	}

    return (
        <>
            {/* fillHeight is a shared sx constant that will make the element fill the available height of its parent */}
            <Box sx={{ ...fillHeight, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {canCreate &&
                    <Button
                        variant="contained"
                        size="input"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        {strings.organizationNewTitle}
                    </Button>
                    }
                </Box>
                <Spacer y={2} />
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGrid                
                        rows={organizationList}
                        columns={columns}
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={handleRowSelectionModelChange}
                        autoPageSize={true}
                        loading={isGridLoading}
                        localeText={{ noRowsLabel: strings.organizationsNotFound }}
                    />
                 </Box>
            </Box>
            
            {/* Wrap the dialog with a form so that all of its fields can be registered with react-hook-form (NOTE: The formId param on the dialog must match the id of the form) */}
            <form onSubmit={onFormSubmit(handleSubmit)} id="dialog-org-form">               
                <Dialog                
                    open={dialogOpen}
                    title={isEditing ? strings.organizationEditTitle.replace("{{name}}", dialogTitleName!) : strings.organizationNewTitle}
                    formId="dialog-org-form"
                    formIsDirty={formState.isDirty}
                    saving={isSaving}
                    saveButtonTitle={strings.save}
                    readOnly={dialogReadOnly}
                    onCancel={() => setDialogOpen(false)}
                    cancelButtonTitle={strings.cancel}
                >
                    {/* Setup TextField, the ...register function registers the field with react-hook-form (including validation) */}
                    <FormTextField
                        id="name"
                        label={strings.name}
                        variant="outlined"
                        sx={{ width: 400 }}
                        {...register("name", {
                            required: strings.validationNameRequired
                        })}
                        isEditing={isEditing}                        
                        error={!!formState.errors.name}
                        helperText={formState.errors.name?.message || " "}
                    />
                </Dialog>  
            </form>

            <InviteUserDialog
                organizationId={selectedOrganizationId!}
                open={userDialogOpen}
                onDone={handleUserClose}
                onCancel={handleUserClose}
            />
        </>
    );
};

export default OrganizationList;